import {
  GetInstallationHeaderMetricsDocument,
  GetInstallationHeaderMetricsQuery,
  GetProjectInstallationsDocument,
  GetProjectInstallationsQuery,
  SaveInstallation_Input,
  SaveInstallationMutation,
  useSaveInstallationFormQuery,
  useSaveInstallationMutation
} from 'generated/graphql';
import _ from 'lodash';
import {
  appFormUtil,
  gqlCacheUtil
} from 'modules/common';

import React, { useEffect } from 'react';


export type IFormState = {
  areaId: string;
  durationInDays: string;
  endDate: string;
  estimatedStartDate: string;
  id: string;
  installationTypeId: string;
  name: string;
  projectId: string;
  purchaseDate: string;
  roomName: string;
  startDate: string;
  statusId: string;
  subAreaId: string;
  connectedToMilestone?: boolean;
  milestoneId: string;
};

const initialState = {
  areaId: '',
  durationInDays: '',
  endDate: '',
  estimatedStartDate: '',
  id: '',
  installationTypeId: '',
  name: '',
  roomName: '',
  projectId: '',
  purchaseDate: '',
  startDate: '',
  statusId: '',
  subAreaId: '',
  milestoneId: '',
  connectedToMilestone: false
};

export function useFormState(id: string, projectId: string) {
  const { formState, dispatch, genFieldProps } = appFormUtil.useFormState({
    initialState,
    key2FieldConfig: {
      areaId: {
        label: 'Area id',
        type: 'text',
        required: true
      },
      durationInDays: {
        label: 'Duration in days',
        type: 'number'
      },
      endDate: {
        label: 'End date',
        type: 'date'
      },
      estimatedStartDate: {
        label: 'Estimated start date',
        type: 'date',
        required: true
      },
      id: {
        label: 'Id',
        type: 'text'
      },
      installationTypeId: {
        label: 'Type of Installation',
        type: 'text',
        required: true
      },
      roomName: {
        label: 'Room',
        type: 'text',
        required: false
      },
      name: {
        label: 'Name',
        type: 'text',
        required: true
      },
      milestoneId: {
        label: 'Milestone',
        type: 'text'
      },
      projectId: {
        label: 'Project id',
        type: 'text'
      },
      purchaseDate: {
        label: 'Purchase date',
        type: 'date'
      },
      startDate: {
        label: 'Start date',
        type: 'date'
      },
      statusId: {
        label: 'Status id',
        type: 'text'
      },
      subAreaId: {
        label: 'Sub area id',
        type: 'text'
      }
    }
  });

  useEffect(() => {
    if (projectId !== '' && formState.projectId === '') {
      dispatch({
        type: 'UPDATE',
        payload: {
          property: 'projectId',
          value: projectId
        }
      });
    }
  }, [
    dispatch,
    formState.projectId,
    projectId
  ]);



  const { data, loading, called } = useQuery(id);

  useEffect(() => {
    if (data.id !== '' && formState.id === '' && !loading && called) {
      dispatch({
        type: 'BATCH_UPDATE',
        payload: {
          ...formState,
          ...data
        }
      });
    }

  }, [
    data,
    loading,
    called,
    dispatch,
    formState
  ]);

  return React.useMemo(
    () => {

      return {
        formState,
        dispatch,
        genFieldProps
      };
    },
    [
      dispatch,
      formState,
      genFieldProps
    ]
  );
}

export function useSaveInstallation() {
  const [
    fnMutate,
    tuple
  ] = useSaveInstallationMutation({
    refetchQueries: []
  });
  const fnWrappedMutate = React.useCallback(
    async (input: SaveInstallation_Input) => {
      await fnMutate({
        variables: {
          input
        },
        update: (cache, { }) => {
          cache.modify({
            fields: {
              project: (existing, { readField }) => {
                // ! this needs to be investigated but seems to ensure that react app refreshes
                /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
                const installations = readField('installations', existing);

              }
            }
          });
        },

        updateQueries: {
          ...gqlCacheUtil.genUpdateQueryMap<GetProjectInstallationsQuery, SaveInstallationMutation>(
            GetProjectInstallationsDocument, (prevState, { mutationResult, queryVariables }) => {
              if (!prevState.project?.installations) {
                return prevState;
              }
              if (!mutationResult.data?.saveInstallation) {
                return prevState;
              }

              if (queryVariables.id === mutationResult.data.saveInstallation.id) {
                return {
                  ...prevState,
                  project: {
                    ...prevState.project,
                    installations: _.compact([
                      ...prevState.project.installations,
                      mutationResult.data.saveInstallation
                    ])
                  }
                };
              } else {
                const updatedInstallations = prevState.project.installations.map((node) => {
                  if (node.id === mutationResult.data?.saveInstallation?.id) {
                    return {
                      ...node,
                      ...mutationResult.data.saveInstallation
                    };
                  }
                  return node;
                });
                return {
                  ...prevState,
                  project: {
                    ...prevState.project,
                    installations: updatedInstallations
                  }
                };
              }

            }
          )
        },
        ...gqlCacheUtil.genUpdateQueryMap<GetInstallationHeaderMetricsQuery, SaveInstallationMutation>(
          GetInstallationHeaderMetricsDocument, (prevState, { mutationResult }) => {
            if (!prevState?.project?.installations) {
              return prevState;
            }

            return {
              ...prevState,
              project: {
                ...prevState.project,
                installations: _.compact([
                  ...prevState.project.installations,
                  mutationResult.data?.saveInstallation
                ]),
                installationsWithAllDecisionsComplete: _.compact([
                  ...prevState.project.installationsWithAllDecisionsComplete,
                  mutationResult.data?.saveInstallation
                ])

              }
            };


            // return prevState;
          }
        )
      });
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}

export interface IData extends Omit<IFormState, 'projectId'> {}

export function useQuery(id: string) {
  const tuple = useSaveInstallationFormQuery({
    variables: {
      id
    },
    skip: id === ''
  });

  return React.useMemo(
    () => {
      const installation = tuple.data?.installation;
      const data: IData = {
        id: installation?.id ?? '',
        areaId: installation?.area.id ?? '',
        subAreaId: installation?.subArea.id ?? '',
        durationInDays: `${installation?.durationInDays ?? 0}`,
        endDate: `${installation?.endDate && installation.endDate.toFormat('yyyy-LL-dd')}`,
        startDate: `${installation?.startDate && installation.startDate.toFormat('yyyy-LL-dd')}`,
        estimatedStartDate: `${installation?.estimatedStartDate && installation.estimatedStartDate.toFormat('yyyy-LL-dd')}`,
        installationTypeId: installation?.installationType.id ?? '',
        name: installation?.name ?? '',
        purchaseDate: installation?.purchaseDate?.toFormat('yyyy-LL-dd') ?? '',
        statusId: installation?.status.id ?? '',
        connectedToMilestone: installation?.milestone?.id ? true : false,
        milestoneId: installation?.milestone?.id ?? '',
        roomName: installation?.roomName ?? ''
      };

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}
